import React from "react";
import Icon from "react-icons-kit";
import {
  StyleSheet,
  View,
  StyleProp,
  ViewStyle,
  TouchableHighlight,
  TextStyle,
} from "react-native";

import { Text } from "UpduoLibrary";
import {
  upduoPrimaryBlue,
  upduoPrimaryDark,
  upduoLighterGray,
} from "config/UpduoColors";
import { DotLoader } from "react-spinners";

export enum ButtonType {
  PRIMARY,
  SECONDARY,
  LINK,
}

export enum ButtonSize {
  LARGE,
  SMALL,
}

const ACTIVE_COLORS: Record<ButtonType, string> = {
  [ButtonType.PRIMARY]: "#272f47",
  [ButtonType.SECONDARY]: upduoPrimaryBlue,
  [ButtonType.LINK]: "transparent",
};

const defaultButtonStyles = StyleSheet.create({
  base: {
    color: "white",
  },
  [ButtonSize.LARGE]: {
    color: "white",
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 30,
    fontSize: 16,
    fontWeight: "700",
  },
  [ButtonSize.SMALL]: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    borderRadius: 20,
    fontSize: 14,
    fontWeight: "bold",
  },
});

const disabledButtonStyles = StyleSheet.create({
  base: {},
  [ButtonType.PRIMARY]: {
    backgroundColor: upduoLighterGray,
  },
  [ButtonType.SECONDARY]: {
    backgroundColor: upduoLighterGray,
  },
  [ButtonType.LINK]: {
    fontWeight: "400",
    color: upduoLighterGray,
  },
});

const enabledButtonStyles = StyleSheet.create({
  base: { cursor: "pointer" } as any,
  [ButtonType.PRIMARY]: {
    backgroundColor: upduoPrimaryDark,
  },
  [ButtonType.SECONDARY]: {
    backgroundColor: upduoPrimaryBlue,
  },
  [ButtonType.LINK]: {
    fontWeight: "400",
    color: upduoPrimaryBlue,
  },
});

export default function UpduoButton({
  text,
  onPress,
  style: userStyle,
  textStyle,
  disabled = false,
  type = ButtonType.PRIMARY,
  size = ButtonSize.LARGE,
  loading,
  icon,
}: {
  text: string;
  onPress: () => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  type?: ButtonType;
  size?: ButtonSize;
  loading?: boolean;
  icon?: any;
}) {
  const buttonStyle =
    disabled || loading ? disabledButtonStyles : enabledButtonStyles;
  const iconSize = size === ButtonSize.LARGE ? 24 : 20;

  return (
    <TouchableHighlight
      style={[
        defaultButtonStyles.base,
        defaultButtonStyles[size],
        buttonStyle.base,
        buttonStyle[type],
        userStyle,
      ]}
      onPress={onPress}
      disabled={disabled || loading}
      underlayColor={ACTIVE_COLORS[type]}
      activeOpacity={type === ButtonType.LINK ? 0.5 : undefined}
    >
      <View
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading ? (
          <DotLoader color={"white"} size={iconSize} loading={true} />
        ) : (
          <>
            {icon ? (
              <Icon
                icon={icon}
                style={{ marginRight: 6, verticalAlign: "middle" }}
                size={iconSize}
              />
            ) : null}
            <Text
              isHeading={true}
              style={[
                {
                  color: "inherit",
                  fontSize: "inherit" as any,
                  textTransform: "uppercase",
                  fontWeight: "inherit" as any,
                  letterSpacing: ".1em" as any,
                },
                textStyle,
              ]}
            >
              {text}
            </Text>
          </>
        )}
      </View>
    </TouchableHighlight>
  );
}
